import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/landing/Navbar';
import { Footer } from '../components/landing/Footer';
import LogoImage from '../assets/LogoFiverFlow.png';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen text-[rgb(var(--fg))] relative overflow-hidden bg-black">
      <div className="relative z-10">
        <Navbar />
        
        <main className="max-w-4xl mx-auto px-4 md:px-8 py-16 mt-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Header */}
            <div className="mb-12">
              <Link to="/home" className="inline-flex items-center mb-6 text-neutral-400 hover:text-[rgb(var(--fg))] transition-colors">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Home
              </Link>
              <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-indigo-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Terms of Service
              </h1>
              <p className="text-neutral-400 text-lg">
                Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>

            {/* Content */}
            <div className="prose prose-invert prose-lg max-w-none space-y-8">
              <section>
                <p className="text-neutral-300 leading-relaxed">
                  Welcome to FiverFlow. These Terms of Service ("Terms") govern your access to and use of 
                  FiverFlow's website, applications, and services (collectively, the "Service"). By accessing 
                  or using our Service, you agree to be bound by these Terms. If you disagree with any part 
                  of these Terms, you may not access the Service.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">1. Acceptance of Terms</h2>
                <p className="text-neutral-300 leading-relaxed">
                  By creating an account, accessing, or using FiverFlow, you acknowledge that you have read, 
                  understood, and agree to be bound by these Terms and our Privacy Policy. If you are using 
                  the Service on behalf of an organization, you represent and warrant that you have the 
                  authority to bind that organization to these Terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">2. Account Registration and Security</h2>
                <div className="space-y-4">
                  <p className="text-neutral-300 leading-relaxed">
                    To use certain features of the Service, you must register for an account. When you register, 
                    you agree to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-neutral-300 ml-4">
                    <li>Provide accurate, current, and complete information about yourself</li>
                    <li>Maintain and promptly update your account information</li>
                    <li>Maintain the security of your password and identification</li>
                    <li>Accept all responsibility for activities that occur under your account</li>
                    <li>Notify us immediately of any unauthorized use of your account</li>
                    <li>Be at least 18 years of age or have the consent of a parent or guardian</li>
                  </ul>
                  <p className="text-neutral-300 leading-relaxed">
                    You are responsible for maintaining the confidentiality of your account credentials and for 
                    all activities that occur under your account. FiverFlow cannot and will not be liable for 
                    any loss or damage arising from your failure to comply with this security obligation.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">3. Use of Service</h2>
                <div className="space-y-4">
                  <p className="text-neutral-300 leading-relaxed">
                    Subject to your compliance with these Terms, FiverFlow grants you a limited, non-exclusive, 
                    non-transferable, revocable license to access and use the Service for your personal or 
                    internal business purposes.
                  </p>
                  <p className="text-neutral-300 leading-relaxed font-semibold">
                    You agree not to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-neutral-300 ml-4">
                    <li>Use the Service for any illegal purpose or in violation of any applicable laws</li>
                    <li>Attempt to gain unauthorized access to the Service or any related systems or networks</li>
                    <li>Interfere with or disrupt the integrity or performance of the Service</li>
                    <li>Transmit any viruses, malware, or other harmful code</li>
                    <li>Use the Service to violate, infringe, or misappropriate any intellectual property rights</li>
                    <li>Harass, abuse, or harm other users or third parties</li>
                    <li>Collect or store personal data about other users without their permission</li>
                    <li>Use automated systems or bots to access the Service without authorization</li>
                    <li>Resell, sublicense, or otherwise distribute the Service to third parties</li>
                    <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">4. Subscription Plans and Payment</h2>
                <div className="space-y-4">
                  <p className="text-neutral-300 leading-relaxed">
                    FiverFlow offers various subscription plans with different features and limitations. By 
                    subscribing to a paid plan, you agree to pay the fees specified for your selected plan.
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-neutral-300 ml-4">
                    <li>Subscription fees are billed in advance on a monthly or annual basis</li>
                    <li>Fees are non-refundable except as required by applicable law</li>
                    <li>We reserve the right to change our pricing with 30 days' notice to existing subscribers</li>
                    <li>You are responsible for all taxes applicable to your subscription</li>
                    <li>If payment fails, we may suspend or terminate your access to paid features</li>
                    <li>You may cancel your subscription at any time, but you will continue to have access 
                        until the end of your current billing period</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">5. Content and Intellectual Property</h2>
                <div className="space-y-4">
                  <p className="text-neutral-300 leading-relaxed">
                    The Service, including its original content, features, functionality, design, and software, 
                    is owned by FiverFlow and is protected by international copyright, trademark, patent, trade 
                    secret, and other intellectual property laws.
                  </p>
                  <p className="text-neutral-300 leading-relaxed">
                    You retain ownership of any content you create, upload, or store using the Service ("User Content"). 
                    By using the Service, you grant FiverFlow a worldwide, non-exclusive, royalty-free license to use, 
                    reproduce, modify, and display your User Content solely for the purpose of providing and improving 
                    the Service.
                  </p>
                  <p className="text-neutral-300 leading-relaxed">
                    You represent and warrant that you own or have the necessary rights to all User Content you 
                    provide and that such content does not violate any third-party rights or applicable laws.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">6. Data and Privacy</h2>
                <p className="text-neutral-300 leading-relaxed">
                  Your use of the Service is also governed by our Privacy Policy. By using the Service, you 
                  consent to the collection, use, and disclosure of your information as described in our Privacy 
                  Policy. We are committed to protecting your privacy and will handle your data in accordance 
                  with applicable privacy laws, including the Personal Information Protection and Electronic 
                  Documents Act (PIPEDA) in Canada.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">7. Service Availability and Modifications</h2>
                <div className="space-y-4">
                  <p className="text-neutral-300 leading-relaxed">
                    We strive to provide reliable service, but we do not guarantee that the Service will be 
                    available, uninterrupted, secure, or error-free. We reserve the right to modify, suspend, 
                    or discontinue the Service (or any part thereof) at any time with or without notice.
                  </p>
                  <p className="text-neutral-300 leading-relaxed">
                    We may update or modify the Service from time to time to improve functionality, security, 
                    or for other business reasons. We will endeavor to provide notice of significant changes, 
                    but you acknowledge that we are not obligated to do so.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">8. Termination</h2>
                <div className="space-y-4">
                  <p className="text-neutral-300 leading-relaxed">
                    You may terminate your account at any time by contacting us or using the account deletion 
                    feature in your settings. We may suspend or terminate your account immediately, without 
                    prior notice, if you violate these Terms or engage in fraudulent, illegal, or harmful activity.
                  </p>
                  <p className="text-neutral-300 leading-relaxed">
                    Upon termination, your right to use the Service will immediately cease. We may delete your 
                    account and data after a reasonable retention period, subject to our legal obligations. 
                    Provisions of these Terms that by their nature should survive termination will survive, 
                    including ownership provisions, warranty disclaimers, and limitations of liability.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">9. Limitation of Liability</h2>
                <div className="space-y-4">
                  <p className="text-neutral-300 leading-relaxed">
                    TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, FIVERFLOW SHALL NOT BE LIABLE FOR ANY 
                    INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS 
                    OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, 
                    OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OF OR INABILITY TO USE THE SERVICE.
                  </p>
                  <p className="text-neutral-300 leading-relaxed">
                    FIVERFLOW'S TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THE USE OF 
                    OR ANY INABILITY TO USE ANY PORTION OF THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID TO 
                    FIVERFLOW IN THE 12 MONTHS PRIOR TO THE ACTION GIVING RISE TO LIABILITY, OR $100, WHICHEVER 
                    IS GREATER.
                  </p>
                  <p className="text-neutral-300 leading-relaxed">
                    Some jurisdictions do not allow the exclusion or limitation of liability for consequential 
                    or incidental damages, so the above limitation may not apply to you.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">10. Disclaimer of Warranties</h2>
                <p className="text-neutral-300 leading-relaxed">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER 
                  EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, 
                  FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. FIVERFLOW DOES NOT WARRANT THAT THE 
                  SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE, OR THAT DEFECTS WILL BE CORRECTED.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">11. Indemnification</h2>
                <p className="text-neutral-300 leading-relaxed">
                  You agree to indemnify, defend, and hold harmless FiverFlow and its officers, directors, 
                  employees, and agents from and against any claims, liabilities, damages, losses, and expenses, 
                  including reasonable attorneys' fees, arising out of or in any way connected with your access 
                  to or use of the Service, your violation of these Terms, or your infringement of any rights 
                  of another party.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">12. Governing Law and Dispute Resolution</h2>
                <div className="space-y-4">
                  <p className="text-neutral-300 leading-relaxed">
                    These Terms shall be governed by and construed in accordance with the laws of Canada, without 
                    regard to its conflict of law provisions. Any disputes arising out of or relating to these 
                    Terms or the Service shall be resolved in the courts of Canada.
                  </p>
                  <p className="text-neutral-300 leading-relaxed">
                    Before filing a claim, you agree to try to resolve the dispute informally by contacting us 
                    at legal@fiverflow.com. We will try to resolve the dispute informally within 60 days.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">13. Changes to Terms</h2>
                <p className="text-neutral-300 leading-relaxed">
                  We reserve the right to modify these Terms at any time. If we make material changes, we will 
                  notify you by email or through a notice on the Service. Your continued use of the Service 
                  after such modifications constitutes your acceptance of the updated Terms. If you do not agree 
                  to the modified Terms, you must stop using the Service.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">14. Contact Information</h2>
                <p className="text-neutral-300 leading-relaxed">
                  If you have any questions about these Terms, please contact us at:
                </p>
                <div className="mt-4 p-4 bg-neutral-900 rounded-lg border border-white/10">
                  <p className="text-neutral-300">
                    <strong>FiverFlow</strong><br />
                    Email: legal@fiverflow.com<br />
                    Website: <Link to="/home" className="text-indigo-400 hover:text-indigo-300">https://fiverflow.com</Link>
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-[rgb(var(--fg))] mt-8 mb-4">15. Miscellaneous</h2>
                <div className="space-y-4">
                  <p className="text-neutral-300 leading-relaxed">
                    These Terms constitute the entire agreement between you and FiverFlow regarding the Service 
                    and supersede all prior agreements and understandings.
                  </p>
                  <p className="text-neutral-300 leading-relaxed">
                    If any provision of these Terms is found to be unenforceable or invalid, that provision 
                    shall be limited or eliminated to the minimum extent necessary, and the remaining provisions 
                    shall remain in full force and effect.
                  </p>
                  <p className="text-neutral-300 leading-relaxed">
                    FiverFlow's failure to enforce any right or provision of these Terms shall not be deemed a 
                    waiver of such right or provision.
                  </p>
                  <p className="text-neutral-300 leading-relaxed">
                    You may not assign or transfer these Terms or your rights hereunder without our prior written 
                    consent. We may assign these Terms without restriction.
                  </p>
                </div>
              </section>
            </div>
          </motion.div>
        </main>

        <Footer />
      </div>
    </div>
  );
}

